// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { WarrantyFormContext } from "@store/Library/Widgets/Warranty/WarrantyForm";
import React from "react";
import { css } from "styled-components";
import { connect, ResolveThunks } from "react-redux";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@store/Framework/Store/Store/Components/WarrantyForm/Handlers/SetFieldValue";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import getColor from "@insite/mobius/utilities/getColor";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import DatePicker, { DatePickerState } from "@insite/mobius/DatePicker";
import {
    getCountriesDataView,
    getCurrentCountries,
} from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import Loader from "@store/Library/Components/Toolkit/Loader";
import debounce from "lodash/debounce";
import constants from "@store/constants";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

interface DebouncedFunc<T extends (...args: any[]) => any> {
    /**
     * Call the original function, but applying the debounce rules.
     *
     * If the debounced function can be run immediately, this calls it and returns its return
     * value.
     *
     * Otherwise, it returns the return value of the last invocation, or undefined if the debounced
     * function was not invoked yet.
     */
    (...args: Parameters<T>): ReturnType<T> | undefined;

    /**
     * Throw away any pending invocation of the debounced function.
     */
    cancel(): void;

    /**
     * If there is a pending invocation of the debounced function, invoke it immediately and return
     * its return value.
     *
     * Otherwise, return the value from the last invocation, or undefined if the debounced function
     * was never invoked.
     */
    flush(): ReturnType<T> | undefined;
}

interface OwnProps {
    extendedStyles?: WarrantyFormFieldStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { userName, email } = state.context.session;
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);
    return {
        fieldValues: state.store.components.warrantyForm.fieldValues,
        shouldLoadCountries: !countriesDataView.value && !countriesDataView.isLoading,
        countries: getCurrentCountries(state),
        currentCountry: state.context.session.currency,
        isSignedIn: userName && !currentUserIsGuest,
        email,
    };
};

const mapDispatchToProps = {
    setFieldValue,
    loadCurrentCountries,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface WarrantyFormFieldStyles {
    form?: InjectableCss;
    container?: GridContainerProps;
    sectionContainer?: GridContainerProps;
    formGridItem?: GridItemProps;
    thirdsGridItem?: GridItemProps;
    halfGridItem?: GridItemProps;
    fullGridItem?: GridItemProps;
    textFieldText?: TextFieldPresentationProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    successBox?: InfosBoxProps;
    modalContainer?: GridContainerProps;
    emphasisContainer?: InjectableCss;
    tinyContainer?: InjectableCss;
    checkboxGroup?: CheckboxGroupProps;
    sectionTitle?: TypographyPresentationProps;
    certifyTitle?: TypographyPresentationProps;
}

export const warrantyFormFieldStyles: WarrantyFormFieldStyles = {
    container: {
        css: css`
            form {
                width: 100%;
            }
        `,
    },
    sectionContainer: {
        gap: 10,
        css: css`
            border: 2px solid ${getColor("common.border")};
            padding: ${pxToRem(20)};
        `,
    },
    formGridItem: { width: 12 },
    thirdsGridItem: { width: [12, 12, 4, 4, 4] },
    halfGridItem: { width: [12, 12, 6, 6, 6] },
    fullGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            width: 100%;
        `,
    },
    successBox: {
        type: "success",
    },
    emphasisContainer: {
        css: css`
            background: ${getColor("common.accent")};
            padding: 2em;
        `,
    },
    tinyContainer: {
        css: css`
            margin: 1em 0 0.8em 0;
            margin-bottom: 1em;
            zoom: 0.85;
        `,
    },
    checkboxGroup: {
        css: css`
            flex-grow: 1;
        `,
    },
    sectionTitle: {
        variant: "h4",
        forwardAs: "h2",
    },
    certifyTitle: {
        variant: "h3",
        forwardAs: "p",
        css: css`
            text-transform: uppercase;
        `,
    },
};

const fieldConfig: Record<
    string,
    {
        label: string;
        groupValidation?: string; // all fields need a value to be validated
        requiredParentFieldKey?: string; // parent field needs a value to be valdiated
        requiredParentFieldValue?: string; // parent field needs a value to be valdiated
        placeholder: string;
        required: boolean;
        regex?: RegExp;
        regexErrorMessage?: string;
    }
> = constants.warrantyFormFields;

const WarrantyFormFields = ({
    setFieldValue,
    fieldValues,
    countries,
    isSignedIn,
    email,
    extendedStyles,
    shouldLoadCountries,
    loadCurrentCountries,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(warrantyFormFieldStyles, extendedStyles));
    const [localFieldValues, setLocalFieldValues] = React.useState<Record<string, string>>({}); // Mirrors actual values, to be used before debounce happen
    const debounceMapRef = React.useRef<Record<string, DebouncedFunc<(key: string, value: string) => void>>>({});
    const warrantyFormContext = React.useContext(WarrantyFormContext);
    const [errorMessages, setErrorMessages] = React.useState<Record<string, string>>({});

    const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);

    React.useEffect(() => {
        if (shouldLoadCountries) {
            loadCurrentCountries();
        }
    }, [countries]);

    React.useEffect(() => {
        const today = new Date().toISOString().split("T")[0];
        setFieldValue({ key: "submittedFormDate", value: today });
        setLocalFieldValues(prev => ({ ...prev, ["submittedFormDate"]: today }));
        if (email) {
            setFieldValue({ key: "clientEmail", value: email });
            setLocalFieldValues(prev => ({ ...prev, ["clientEmail"]: email }));
        }
    }, []);

    React.useEffect(() => {
        Object.entries(fieldConfig).forEach(([key, config]) => {
            // Skip group-based fields here
            if (config.groupValidation) return;

            const value = fieldValues[key];
            const emptyTheValue = () => {
                setFieldValue({ key, value: "" });
                setFieldError(key, "");
                setLocalFieldValues(prev => ({ ...prev, [key]: "" }));
            };
            // Always validated
            if (config.requiredParentFieldKey) {
                if (fieldValues[config.requiredParentFieldKey] !== config.requiredParentFieldValue) {
                    if (value) {
                        emptyTheValue();
                    }
                    if (warrantyFormContext.validators[key]) {
                        delete warrantyFormContext.validators[key];
                    }
                    return; // Do Not Validate Field
                }
                if (!fieldValues[config.requiredParentFieldKey]) {
                    if (value) {
                        emptyTheValue();
                    }
                    if (warrantyFormContext.validators[key]) {
                        delete warrantyFormContext.validators[key];
                    }
                    return; // Do Not Validate Field
                }
            }

            const validator = () => validateInputField(key, value, config.label);
            warrantyFormContext.validators[key] = validator;

            if (value !== undefined) {
                validator();
            }
        });
    }, [fieldValues]);

    React.useEffect(() => {
        // Values with a group need at least one of the group to have a value before validating
        const groups: Record<string, string[]> = {};

        // Build validation groups from fieldConfig
        for (const key in fieldConfig) {
            const group = fieldConfig[key].groupValidation;
            if (!group) continue;

            if (!groups[group]) {
                groups[group] = [];
            }

            groups[group].push(key);
        }

        // Validate each group
        for (const keys of Object.values(groups)) {
            const hasAnyValue = keys.some(key => fieldValues[key]?.trim());
            if (!hasAnyValue) continue;

            for (const key of keys) {
                const label = fieldConfig[key].label;
                const value = fieldValues[key];

                const validator = () => validateInputField(key, value, label);
                warrantyFormContext.validators[key] = validator;
                validator();
            }
        }
    }, [fieldValues]);

    const requiredDateFieldChangeHandler = (key: string, { selectedDay }: DatePickerState) => {
        setFieldValue({ key, value: selectedDay?.toISOString().split("T")[0] ?? "" });
        setLocalFieldValues(prev => ({
            ...prev,
            ["submittedFormDate"]: selectedDay?.toISOString().split("T")[0] ?? "",
        }));
        setFieldError(key, "");
    };

    const requiredTextFieldChangeHandler = (
        key: string,
        event:
            | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
            | React.FormEvent<HTMLInputElement | HTMLSelectElement>,
    ) => {
        const target = event.target as HTMLInputElement | HTMLSelectElement;
        const value = target.value;

        // Save individual debounces to play well with Chrome's super fast autofill
        if (!debounceMapRef.current[key]) {
            debounceMapRef.current[key] = debounce((key: string, value: string) => {
                setFieldValue({ key, value });
                setFieldError(key, "");
            }, 220);
        }

        setLocalFieldValues(prev => ({ ...prev, [key]: value })); // Display only, to hide lag from debounce
        debounceMapRef.current[key](key, value);

        setTimeout(() => {
            debounceMapRef.current[key].flush(); // Calls the last one always, for Chrome's autofill
        }, 250);
    };

    const requiredTextFieldBlurHandler = (
        key: string,
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const value = event.target.value;
        setLocalFieldValues(prev => ({ ...prev, [key]: value }));
        setFieldValue({ key, value });
    };

    const setFieldError = (field: string, message: string) => {
        setErrorMessages(prev => ({ ...prev, [field]: message }));
    };

    const validateInputField = (fieldKey: string, value: string | undefined, label: string): boolean => {
        let errorMessage = "";
        if (fieldConfig[fieldKey].required) {
            errorMessage = !value?.trim() ? (siteMessage("Field_Required", label) as string) : "";
        }

        // Check for a regex
        if (!errorMessage && fieldConfig[fieldKey].regex instanceof RegExp && value) {
            if (!fieldConfig[fieldKey].regex.test(value)) {
                errorMessage = fieldConfig[fieldKey].regexErrorMessage
                    ? fieldConfig[fieldKey].regexErrorMessage
                    : (siteMessage("Field_Invalid_Format", label) as string);
            }
        }

        setFieldError(fieldKey, errorMessage);
        return !errorMessage;
    };

    const showMorePartsInputs = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
        const el = itemRefs.current[index];
        e.currentTarget.style.display = "none";
        if (el) {
            el.style.display = "block";
        }
    };

    if (!isSignedIn) {
        return null;
    }

    if (!countries || !countries[0].states) {
        return <Loader />;
    }

    return (
        <GridContainer {...styles.container} data-test-selector="warrantyFormFields">
            <GridItem {...styles.fullGridItem} /* Top Bar */>
                <GridContainer>
                    <GridItem {...styles.fullGridItem}>
                        <InfosBox>
                            {translate("Claims must be submitted within 30 days of repair completion date")}
                        </InfosBox>
                    </GridItem>
                    <GridItem {...styles.halfGridItem}>
                        <TextField
                            {...styles.textFieldText}
                            label={fieldConfig.dcneAccountNumber.label}
                            required={fieldConfig.dcneAccountNumber.required}
                            placeholder={fieldConfig.dcneAccountNumber.placeholder}
                            value={localFieldValues.dcneAccountNumber || ""}
                            error={errorMessages.dcneAccountNumber}
                            maxLength={50}
                            onBlur={e => requiredTextFieldBlurHandler("dcneAccountNumber", e)}
                            onChange={e => requiredTextFieldChangeHandler("dcneAccountNumber", e)}
                        />
                    </GridItem>
                </GridContainer>
            </GridItem>

            <GridItem {...styles.fullGridItem} /* Warranty Types Dropdown */>
                <GridContainer>
                    <GridItem {...styles.halfGridItem}>
                        <Select
                            label={fieldConfig.selectedWarrantyType.label}
                            value={localFieldValues.selectedWarrantyType || ""}
                            required={fieldConfig.selectedWarrantyType.required}
                            onBlur={e => requiredTextFieldBlurHandler("selectedWarrantyType", e)}
                            onChange={e => requiredTextFieldChangeHandler("selectedWarrantyType", e)}
                            error={errorMessages.selectedWarrantyType}
                        >
                            <option value="">{translate("Select State")}</option>
                            <option value="OriginalEquipementWarranty">
                                {translate("Original Equipment Warranty")}
                            </option>
                            <option value="ReplacementPartWarranty">{translate("Replacement Part Warranty")}</option>
                            <option value="OutOfWarrantyCompressorCoreTurnIn">
                                {translate("Out-of-Warranty Compressor Core Turn-In")}
                            </option>
                            <option value="SalvageCompressor">{translate("Salvage Compressor")}</option>
                            <option value="PurchasedContract">{translate("Purchased Contract")}</option>
                        </Select>
                    </GridItem>
                    <GridItem {...styles.halfGridItem}>
                        {fieldValues.selectedWarrantyType === "ReplacementPartWarranty" && (
                            <GridContainer>
                                <GridItem {...styles.halfGridItem}>
                                    <DatePicker
                                        label={fieldConfig.replacementPartWarrantyDate.label}
                                        dateTimePickerProps={{
                                            clearIcon: null,
                                        }}
                                        error={errorMessages.replacementPartWarrantyDate}
                                        required={fieldConfig.replacementPartWarrantyDate.required}
                                        onDayChange={dateTime => {
                                            requiredDateFieldChangeHandler("replacementPartWarrantyDate", dateTime);
                                        }}
                                    />
                                </GridItem>
                                <GridItem {...styles.halfGridItem}>
                                    <TextField
                                        {...styles.textFieldText}
                                        label={fieldConfig.replacementPartWarrantyInvoiceNumber.label}
                                        required={fieldConfig.replacementPartWarrantyInvoiceNumber.required}
                                        placeholder={fieldConfig.replacementPartWarrantyInvoiceNumber.placeholder}
                                        value={localFieldValues.replacementPartWarrantyInvoiceNumber || ""}
                                        error={errorMessages.replacementPartWarrantyInvoiceNumber}
                                        onBlur={e =>
                                            requiredTextFieldBlurHandler("replacementPartWarrantyInvoiceNumber", e)
                                        }
                                        onChange={e =>
                                            requiredTextFieldChangeHandler("replacementPartWarrantyInvoiceNumber", e)
                                        }
                                    />
                                </GridItem>
                            </GridContainer>
                        )}
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem {...styles.fullGridItem} /* Addresses */>
                <GridContainer>
                    <GridItem {...styles.halfGridItem}>
                        <GridContainer {...styles.sectionContainer}>
                            <GridItem {...styles.fullGridItem}>
                                <Typography {...styles.sectionTitle}>{translate("Your Company")}</Typography>
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.companyName.label}
                                    required={fieldConfig.companyName.required}
                                    placeholder={fieldConfig.companyName.placeholder}
                                    value={localFieldValues.companyName || ""}
                                    error={errorMessages.companyName}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("companyName", e)}
                                    onChange={e => requiredTextFieldChangeHandler("companyName", e)}
                                />
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.companyStreet.label}
                                    required={fieldConfig.companyStreet.required}
                                    placeholder={fieldConfig.companyStreet.placeholder}
                                    value={localFieldValues.companyStreet || ""}
                                    error={errorMessages.companyStreet}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("companyStreet", e)}
                                    onChange={e => requiredTextFieldChangeHandler("companyStreet", e)}
                                />
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.companyCity.label}
                                    required={fieldConfig.companyCity.required}
                                    placeholder={fieldConfig.companyCity.placeholder}
                                    value={localFieldValues.companyCity || ""}
                                    error={errorMessages.companyCity}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("companyCity", e)}
                                    onChange={e => requiredTextFieldChangeHandler("companyCity", e)}
                                />
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <Select
                                    label={fieldConfig.companySt.label}
                                    value={localFieldValues.companySt || ""}
                                    required={fieldConfig.companySt.required}
                                    onBlur={e => requiredTextFieldBlurHandler("companySt", e)}
                                    onChange={e => requiredTextFieldChangeHandler("companySt", e)}
                                    error={errorMessages.companySt}
                                    data-test-selector="customerAddressForm_state"
                                >
                                    <option value="">{translate("Select State")}</option>
                                    {countries[0].states.map(st => (
                                        <option key={st.id} value={st.name}>
                                            {st.name}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.companyZip.label}
                                    required={fieldConfig.companyZip.required}
                                    placeholder={fieldConfig.companyZip.placeholder}
                                    value={localFieldValues.companyZip || ""}
                                    error={errorMessages.companyZip}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("companyZip", e)}
                                    onChange={e => requiredTextFieldChangeHandler("companyZip", e)}
                                />
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.companyPhone.label}
                                    required={fieldConfig.companyPhone.required}
                                    placeholder={fieldConfig.companyPhone.placeholder}
                                    value={localFieldValues.companyPhone || ""}
                                    error={errorMessages.companyPhone}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("companyPhone", e)}
                                    onChange={e => requiredTextFieldChangeHandler("companyPhone", e)}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.halfGridItem}>
                        <GridContainer {...styles.sectionContainer}>
                            <GridItem {...styles.fullGridItem}>
                                <Typography {...styles.sectionTitle}>{translate("Job Site")}</Typography>
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.jobSiteName.label}
                                    required={fieldConfig.jobSiteName.required}
                                    placeholder={fieldConfig.jobSiteName.placeholder}
                                    value={localFieldValues.jobSiteName || ""}
                                    error={errorMessages.jobSiteName}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("jobSiteName", e)}
                                    onChange={e => requiredTextFieldChangeHandler("jobSiteName", e)}
                                />
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.jobSiteStreet.label}
                                    required={fieldConfig.jobSiteStreet.required}
                                    placeholder={fieldConfig.jobSiteStreet.placeholder}
                                    value={localFieldValues.jobSiteStreet || ""}
                                    error={errorMessages.jobSiteStreet}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("jobSiteStreet", e)}
                                    onChange={e => requiredTextFieldChangeHandler("jobSiteStreet", e)}
                                />
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.jobSiteCity.label}
                                    required={fieldConfig.jobSiteCity.required}
                                    placeholder={fieldConfig.jobSiteCity.placeholder}
                                    value={localFieldValues.jobSiteCity || ""}
                                    error={errorMessages.jobSiteCity}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("jobSiteCity", e)}
                                    onChange={e => requiredTextFieldChangeHandler("jobSiteCity", e)}
                                />
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <Select
                                    label={fieldConfig.jobSiteSt.label}
                                    value={localFieldValues.jobSiteSt || ""}
                                    required={fieldConfig.jobSiteSt.required}
                                    onBlur={e => requiredTextFieldBlurHandler("jobSiteSt", e)}
                                    onChange={e => requiredTextFieldChangeHandler("jobSiteSt", e)}
                                    error={errorMessages.jobSiteSt}
                                    data-test-selector="customerAddressForm_state"
                                >
                                    <option value="">{translate("Select State")}</option>
                                    {countries[0].states.map(st => (
                                        <option key={st.id} value={st.name}>
                                            {st.name}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.jobSiteZip.label}
                                    required={fieldConfig.jobSiteZip.required}
                                    placeholder={fieldConfig.jobSiteZip.placeholder}
                                    value={localFieldValues.jobSiteZip || ""}
                                    error={errorMessages.jobSiteZip}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("jobSiteZip", e)}
                                    onChange={e => requiredTextFieldChangeHandler("jobSiteZip", e)}
                                />
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.jobSitePhone.label}
                                    required={fieldConfig.jobSitePhone.required}
                                    placeholder={fieldConfig.jobSitePhone.placeholder}
                                    value={localFieldValues.jobSitePhone || ""}
                                    error={errorMessages.jobSitePhone}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("jobSitePhone", e)}
                                    onChange={e => requiredTextFieldChangeHandler("jobSitePhone", e)}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem {...styles.fullGridItem} /* Base Unit Model */>
                <GridContainer>
                    <GridItem {...styles.fullGridItem}>
                        <GridContainer>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.baseUnitModelNumber.label}
                                    required={fieldConfig.baseUnitModelNumber.required}
                                    placeholder={fieldConfig.baseUnitModelNumber.placeholder}
                                    value={localFieldValues.baseUnitModelNumber || ""}
                                    error={errorMessages.baseUnitModelNumber}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("baseUnitModelNumber", e)}
                                    onChange={e => requiredTextFieldChangeHandler("baseUnitModelNumber", e)}
                                />
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.baseUnitSerialNumber.label}
                                    required={fieldConfig.baseUnitSerialNumber.required}
                                    placeholder={fieldConfig.baseUnitSerialNumber.placeholder}
                                    value={localFieldValues.baseUnitSerialNumber || ""}
                                    error={errorMessages.baseUnitSerialNumber}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("baseUnitSerialNumber", e)}
                                    onChange={e => requiredTextFieldChangeHandler("baseUnitSerialNumber", e)}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>

            <GridItem {...styles.fullGridItem} /* Dates */>
                <GridContainer>
                    <GridItem {...styles.fullGridItem}>
                        <GridContainer>
                            <GridItem {...styles.thirdsGridItem}>
                                <DatePicker
                                    label={fieldConfig.baseUnitInstallDate.label}
                                    dateTimePickerProps={{
                                        clearIcon: null,
                                    }}
                                    error={errorMessages.baseUnitInstallDate}
                                    required={fieldConfig.baseUnitInstallDate.required}
                                    onDayChange={dateTime => {
                                        requiredDateFieldChangeHandler("baseUnitInstallDate", dateTime);
                                    }}
                                />
                            </GridItem>
                            <GridItem {...styles.thirdsGridItem}>
                                <DatePicker
                                    label={fieldConfig.failDate.label}
                                    dateTimePickerProps={{
                                        clearIcon: null,
                                    }}
                                    error={errorMessages.failDate}
                                    required={fieldConfig.failDate.required}
                                    onDayChange={dateTime => {
                                        requiredDateFieldChangeHandler("failDate", dateTime);
                                    }}
                                />
                            </GridItem>
                            <GridItem {...styles.thirdsGridItem}>
                                <DatePicker
                                    label={fieldConfig.repairCompleteDate.label}
                                    dateTimePickerProps={{
                                        clearIcon: null,
                                    }}
                                    error={errorMessages.repairCompleteDate}
                                    required={fieldConfig.repairCompleteDate.required}
                                    onDayChange={dateTime => {
                                        requiredDateFieldChangeHandler("repairCompleteDate", dateTime);
                                    }}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>

            <GridItem {...styles.fullGridItem} /* Invoices */>
                <GridContainer>
                    <GridItem {...styles.fullGridItem}>
                        <GridContainer>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.replacementDcneInvoiceNumber.label}
                                    required={fieldConfig.replacementDcneInvoiceNumber.required}
                                    placeholder={fieldConfig.replacementDcneInvoiceNumber.placeholder}
                                    value={localFieldValues.replacementDcneInvoiceNumber || ""}
                                    error={errorMessages.replacementDcneInvoiceNumber}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("replacementDcneInvoiceNumber", e)}
                                    onChange={e => requiredTextFieldChangeHandler("replacementDcneInvoiceNumber", e)}
                                />
                            </GridItem>
                            <GridItem {...styles.halfGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.diagnosisBulletinNumber.label}
                                    required={fieldConfig.diagnosisBulletinNumber.required}
                                    placeholder={fieldConfig.diagnosisBulletinNumber.placeholder}
                                    value={localFieldValues.diagnosisBulletinNumber || ""}
                                    error={errorMessages.diagnosisBulletinNumber}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("diagnosisBulletinNumber", e)}
                                    onChange={e => requiredTextFieldChangeHandler("diagnosisBulletinNumber", e)}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>

            <GridItem {...styles.fullGridItem}>
                <GridContainer>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i, index) => (
                        <StyledWrapper
                            key={i}
                            style={{ display: index > 0 ? "none" : "block" }}
                            ref={(el: any) => {
                                itemRefs.current[index] = el;
                            }}
                        >
                            <GridItem {...styles.fullGridItem}>
                                <GridContainer {...styles.sectionContainer}>
                                    <GridItem {...styles.fullGridItem}>
                                        <Typography {...styles.sectionTitle}>
                                            {translate("Part")} {i}:
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.thirdsGridItem}>
                                        <TextField
                                            {...styles.textFieldText}
                                            label={fieldConfig[`failedPartNumber${i}`].label}
                                            required={fieldConfig[`failedPartNumber${i}`].required}
                                            placeholder={fieldConfig[`failedPartNumber${i}`].placeholder}
                                            value={localFieldValues[`failedPartNumber${i}`] || ""}
                                            error={errorMessages[`failedPartNumber${i}`]}
                                            onBlur={e => requiredTextFieldBlurHandler(`failedPartNumber${i}`, e)}
                                            onChange={e => requiredTextFieldChangeHandler(`failedPartNumber${i}`, e)}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.thirdsGridItem}>
                                        <TextField
                                            {...styles.textFieldText}
                                            label={fieldConfig[`failedQty${i}`].label}
                                            required={fieldConfig[`failedQty${i}`].required}
                                            placeholder={fieldConfig[`failedQty${i}`].placeholder}
                                            value={localFieldValues[`failedQty${i}`] || ""}
                                            error={errorMessages[`failedQty${i}`]}
                                            maxLength={50}
                                            onBlur={e => requiredTextFieldBlurHandler(`failedQty${i}`, e)}
                                            onChange={e => requiredTextFieldChangeHandler(`failedQty${i}`, e)}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.thirdsGridItem}>
                                        <TextField
                                            {...styles.textFieldText}
                                            label={fieldConfig[`failedSerialNumber${i}`].label}
                                            required={fieldConfig[`failedSerialNumber${i}`].required}
                                            placeholder={fieldConfig[`failedSerialNumber${i}`].placeholder}
                                            value={localFieldValues[`failedSerialNumber${i}`] || ""}
                                            error={errorMessages[`failedSerialNumber${i}`]}
                                            onBlur={e => requiredTextFieldBlurHandler(`failedSerialNumber${i}`, e)}
                                            onChange={e => requiredTextFieldChangeHandler(`failedSerialNumber${i}`, e)}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.thirdsGridItem}>
                                        <TextField
                                            {...styles.textFieldText}
                                            label={fieldConfig[`failedReplacementPartNumber${i}`].label}
                                            required={fieldConfig[`failedReplacementPartNumber${i}`].required}
                                            placeholder={fieldConfig[`failedReplacementPartNumber${i}`].placeholder}
                                            value={localFieldValues[`failedReplacementPartNumber${i}`] || ""}
                                            error={errorMessages[`failedReplacementPartNumber${i}`]}
                                            onBlur={e =>
                                                requiredTextFieldBlurHandler(`failedReplacementPartNumber${i}`, e)
                                            }
                                            onChange={e =>
                                                requiredTextFieldChangeHandler(`failedReplacementPartNumber${i}`, e)
                                            }
                                        />
                                    </GridItem>
                                    <GridItem {...styles.thirdsGridItem}>
                                        <TextField
                                            {...styles.textFieldText}
                                            label={fieldConfig[`failedReplacementQty${i}`].label}
                                            required={fieldConfig[`failedReplacementQty${i}`].required}
                                            placeholder={fieldConfig[`failedReplacementQty${i}`].placeholder}
                                            value={localFieldValues[`failedReplacementQty${i}`] || ""}
                                            error={errorMessages[`failedReplacementQty${i}`]}
                                            onBlur={e => requiredTextFieldBlurHandler(`failedReplacementQty${i}`, e)}
                                            onChange={e =>
                                                requiredTextFieldChangeHandler(`failedReplacementQty${i}`, e)
                                            }
                                        />
                                    </GridItem>
                                    <GridItem {...styles.thirdsGridItem}>
                                        <TextField
                                            {...styles.textFieldText}
                                            label={fieldConfig[`failedReplacementSerialNumber${i}`].label}
                                            required={fieldConfig[`failedReplacementSerialNumber${i}`].required}
                                            placeholder={fieldConfig[`failedReplacementSerialNumber${i}`].placeholder}
                                            value={localFieldValues[`failedReplacementSerialNumber${i}`] || ""}
                                            error={errorMessages[`failedReplacementSerialNumber${i}`]}
                                            onBlur={e =>
                                                requiredTextFieldBlurHandler(`failedReplacementSerialNumber${i}`, e)
                                            }
                                            onChange={e =>
                                                requiredTextFieldChangeHandler(`failedReplacementSerialNumber${i}`, e)
                                            }
                                        />
                                    </GridItem>
                                    {i !== 10 && (
                                        <GridItem {...styles.fullGridItem}>
                                            <Button
                                                onClick={e => showMorePartsInputs(e, i++)}
                                                data-test-selector="warrantyForm_addPart"
                                            >
                                                {translate("+ Add a part")}
                                            </Button>
                                        </GridItem>
                                    )}
                                </GridContainer>
                            </GridItem>
                        </StyledWrapper>
                    ))}
                </GridContainer>
            </GridItem>
            <GridItem {...styles.fullGridItem} /* Reason */>
                <GridContainer>
                    <GridItem {...styles.fullGridItem}>
                        <TextField
                            {...styles.textFieldText}
                            label={fieldConfig.reasonForRepairFailure.label}
                            required={fieldConfig.reasonForRepairFailure.required}
                            placeholder={fieldConfig.reasonForRepairFailure.placeholder}
                            value={localFieldValues.reasonForRepairFailure || ""}
                            error={errorMessages.reasonForRepairFailure}
                            maxLength={255}
                            onBlur={e => requiredTextFieldBlurHandler("reasonForRepairFailure", e)}
                            onChange={e => requiredTextFieldChangeHandler("reasonForRepairFailure", e)}
                        />
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem {...styles.fullGridItem} /* Invoices */>
                <GridContainer>
                    <GridItem {...styles.fullGridItem}>
                        <GridContainer {...styles.emphasisContainer}>
                            <GridItem {...styles.fullGridItem}>
                                <Typography {...styles.certifyTitle}>
                                    {translate(
                                        "I certify that this is a true and complete statement of work carried out",
                                    )}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.thirdsGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.signedName.label}
                                    required={fieldConfig.signedName.required}
                                    placeholder={fieldConfig.signedName.placeholder}
                                    value={localFieldValues.signedName || ""}
                                    error={errorMessages.signedName}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("signedName", e)}
                                    onChange={e => requiredTextFieldChangeHandler("signedName", e)}
                                />
                            </GridItem>
                            <GridItem {...styles.thirdsGridItem}>
                                <TextField
                                    {...styles.textFieldText}
                                    label={fieldConfig.clientEmail.label}
                                    required={fieldConfig.clientEmail.required}
                                    placeholder={fieldConfig.clientEmail.placeholder}
                                    value={localFieldValues.clientEmail || ""}
                                    error={errorMessages.clientEmail}
                                    maxLength={50}
                                    onBlur={e => requiredTextFieldBlurHandler("clientEmail", e)}
                                    onChange={e => requiredTextFieldChangeHandler("clientEmail", e)}
                                />
                            </GridItem>

                            <GridItem {...styles.thirdsGridItem}>
                                <DatePicker
                                    label={fieldConfig.submittedFormDate.label}
                                    dateTimePickerProps={{
                                        clearIcon: null,
                                        value: new Date(),
                                    }}
                                    disabled
                                    error={errorMessages.submittedFormDate}
                                    required={fieldConfig.submittedFormDate.required}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(WarrantyFormFields);
